import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useUser } from '@/contexts/UserContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Target, DollarSign, Users, TrendingUp } from 'lucide-react';
import { BrandOrganizationTab } from '@/components/brand/BrandOrganizationTab';

// Brand-side preferences. Mirror of brands.preferences JSONB column.
// Removed orphan UI: "Preferred sports" Textarea, "Monthly budget limit"
// Input, "Alert threshold" Input, "Manage Team Members" button — all had
// no state binding or no handler.
interface BrandPreferences {
  campaign_notifications: boolean;
  athlete_discovery: boolean;
  budget_alerts: boolean;
  auto_matching: boolean;
  preferred_demographics: string[];
  budget_range: string;
  campaign_types: string[];
  approval_workflow: boolean;
}

const DEFAULT_PREFS: BrandPreferences = {
  campaign_notifications: true,
  athlete_discovery: true,
  budget_alerts: true,
  auto_matching: false,
  preferred_demographics: ['18-24', '25-34'],
  budget_range: '$10,000 - $50,000',
  campaign_types: ['sponsorship', 'content'],
  approval_workflow: true,
};

const AGE_GROUPS = ['18-24', '25-34', '35-44', '45+'] as const;

const BUDGET_RANGES = [
  '$1,000 - $5,000',
  '$5,000 - $10,000',
  '$10,000 - $50,000',
  '$50,000 - $100,000',
  '$100,000+',
] as const;

export const BrandSettings = () => {
  const { currentUser } = useUser();
  const { toast } = useToast();
  const [settings, setSettings] = useState<BrandPreferences>(DEFAULT_PREFS);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  const loadPreferences = async () => {
    if (!currentUser?.id) return;
    try {
      // brands is keyed by profile_id (not user_id). currentUser.id is the
      // profile row id, so that's what we filter on.
      const { data, error } = await supabase
        .from('brands')
        .select('preferences')
        .eq('profile_id', currentUser.id)
        .maybeSingle();

      if (error) throw error;

      const prefs = data?.preferences as Partial<BrandPreferences> | null | undefined;
      if (prefs && typeof prefs === 'object' && !Array.isArray(prefs)) {
        setSettings({ ...DEFAULT_PREFS, ...prefs });
      }
    } catch (error) {
      console.error('Error loading brand preferences:', error);
    }
  };

  const updateSetting = useCallback(<K extends keyof BrandPreferences>(
    key: K,
    value: BrandPreferences[K],
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const saveSettings = async () => {
    if (!currentUser?.id) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('brands')
        .update({ preferences: settings })
        .eq('profile_id', currentUser.id);

      if (error) throw error;

      toast({
        title: 'Saved',
        description: 'Your brand preferences have been updated.',
      });
    } catch (error) {
      console.error('Error saving brand preferences:', error);
      toast({
        title: 'Error',
        description: 'Failed to save brand preferences. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleDemographic = (age: string, checked: boolean) => {
    if (checked) {
      updateSetting('preferred_demographics', [...settings.preferred_demographics, age]);
    } else {
      updateSetting('preferred_demographics', settings.preferred_demographics.filter(d => d !== age));
    }
  };

  return (
    <Tabs defaultValue="preferences" className="space-y-6">
      <TabsList>
        <TabsTrigger value="preferences">
          <Target className="h-4 w-4 mr-2" />
          Preferences
        </TabsTrigger>
        <TabsTrigger value="organization">
          <Users className="h-4 w-4 mr-2" />
          Organization
        </TabsTrigger>
      </TabsList>

      <TabsContent value="preferences" className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Campaign Preferences
            </CardTitle>
            <CardDescription>
              Set your default campaign and partnership preferences.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="campaign-notifications">Campaign update notifications</Label>
              <Switch
                id="campaign-notifications"
                checked={settings.campaign_notifications}
                onCheckedChange={(checked) => updateSetting('campaign_notifications', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="athlete-discovery">Enable athlete discovery</Label>
              <Switch
                id="athlete-discovery"
                checked={settings.athlete_discovery}
                onCheckedChange={(checked) => updateSetting('athlete_discovery', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="auto-matching">Auto-match with athletes</Label>
              <Switch
                id="auto-matching"
                checked={settings.auto_matching}
                onCheckedChange={(checked) => updateSetting('auto_matching', checked)}
              />
            </div>

            <div className="space-y-2">
              <Label>Default budget range</Label>
              <Select
                value={settings.budget_range}
                onValueChange={(value) => updateSetting('budget_range', value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BUDGET_RANGES.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Target Demographics
            </CardTitle>
            <CardDescription>
              Define your preferred athlete demographics and audience.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Preferred age groups</Label>
              <div className="grid grid-cols-2 gap-2">
                {AGE_GROUPS.map((age) => (
                  <div key={age} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={age}
                      checked={settings.preferred_demographics.includes(age)}
                      onChange={(e) => toggleDemographic(age, e.target.checked)}
                      className="rounded"
                    />
                    <Label htmlFor={age}>{age} years</Label>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Budget Alerts
            </CardTitle>
            <CardDescription>
              Get notified when campaign spending crosses your set thresholds.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="budget-alerts">Budget threshold alerts</Label>
              <Switch
                id="budget-alerts"
                checked={settings.budget_alerts}
                onCheckedChange={(checked) => updateSetting('budget_alerts', checked)}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Workflow & Approvals
            </CardTitle>
            <CardDescription>
              Configure approval workflows for new campaigns.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="approval-workflow">Require approval for campaigns</Label>
              <Switch
                id="approval-workflow"
                checked={settings.approval_workflow}
                onCheckedChange={(checked) => updateSetting('approval_workflow', checked)}
              />
            </div>
          </CardContent>
        </Card>

        <Button onClick={saveSettings} disabled={isLoading} className="w-full">
          {isLoading ? 'Saving...' : 'Save Brand Settings'}
        </Button>
      </TabsContent>

      <TabsContent value="organization">
        <BrandOrganizationTab />
      </TabsContent>
    </Tabs>
  );
};
